/**
 * Routage : pages publiques (QR, signature devis, confirmation visite, RGPD),
 * espaces dédiés (portail client, sous-traitant, super-admin), puis
 * l'application interne — une seule arborescence filtrée par droits
 * (`app/routes.tsx`), sous la coque commune.
 */
import { Suspense, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { pageToResume, rememberPage } from './core/resume-page.js';
import { dismissLaunchScreen } from './core/launch-screen.js';
import { onApiError } from './core/api.js';
import { SuspendedScreen } from './features/subscription/SuspendedScreen.js';
import { DataRecoveryPage } from './features/subscription/DataRecoveryPage.js';
import { DATA_RECOVERY_PATH } from './features/subscription/subscription-hooks.js';
import { isClosedSubscription, useAuth, useCan } from './core/auth.js';
import { externalRouteElements, isExternalPath } from './app/external-routes.js';
import { StepUpProvider } from './components/StepUpModal.js';
import { SignOutProvider } from './components/SignOutGuard.js';
import { Spinner } from './components/ui.js';
import { BrandLoader, startupVariant } from './components/brand-loader.js';
import { MissingFileBoundary, VersionGate } from './components/shell-extras.js';
import { AppShell } from './components/shell/AppShell.js';
import { SettingsDialog } from './components/shell/SettingsDialog.js';
import { useSettingsBackground } from './app/settings-overlay.js';
import { appRouteElements } from './app/routes.js';
import { startSyncLoop } from './offline/sync.js';
import { PUSH_OPEN_PATH } from './features/notifications/notification-data.js';
// Éager : l'écran de connexion, premier écran de tout le monde (§16, Q144).
// Les pages internes sont déclarées par domaine dans `app/routes.tsx`.
import { LoginPage } from './features/auth/LoginPage.js';

export function App(): ReactNode {
  const { me, loading, reload } = useAuth();
  const can = useCan();
  const location = useLocation();
  // Une adresse n'existe que pour qui peut s'en servir ; la vue terrain ne
  // change que l'accueil et la navigation, jamais les droits.
  const modules = me?.enabled_modules;
  const internalRoutes = useMemo(() => appRouteElements({ can, modules: modules ?? [] }), [can, modules]);

  // Suspendu, terminé ou sans abonnement : un seul écran, aucune donnée (le serveur
  // refuse tout). Une assistance MyEquip garde l'application pour aider l'entreprise.
  const suspended = Boolean(me && !me.support_session && isClosedSubscription(me.subscription?.status));

  // Pas de synchronisation pendant une suspension (le serveur la refuserait),
  // ni pendant une assistance MyEquip : les données du membre ne se
  // préchargent pas sur le poste de l'assistance.
  // Une boucle par compte : elle repart quand le compte change.
  const syncAccount = me && !suspended && !me.support_session ? me.id : undefined;
  useEffect(() => (syncAccount ? startSyncLoop() : undefined), [syncAccount]);

  // Suspension survenue pendant la session : le profil rechargé affiche l'écran.
  useEffect(() => onApiError((code) => {
    if (code === 'compte_suspendu' && !suspended) void reload();
  }), [suspended, reload]);

  const external = isExternalPath(location.pathname);
  // Fenêtre des paramètres (ordinateur) : la page d'où l'on vient reste en fond,
  // la page des paramètres s'affiche par-dessus (app/settings-overlay.ts).
  const background = useSettingsBackground(location);
  const overlay = background && me && !suspended ? background : undefined;
  useEffect(() => {
    if (!loading || external) dismissLaunchScreen();
  }, [loading, external]);

  // Application installée relancée par le téléphone : rouvrir la dernière page
  // plutôt que l'accueil, une seule fois au démarrage.
  const navigate = useNavigate();
  const resumeChecked = useRef(false);
  const userId = me?.id;
  // Autre entreprise du compte (sélecteur, autre onglet, accès retiré) : la page
  // ouverte appartenait à la précédente, on repart de l'accueil. L'ouverture d'un
  // avis push garde sa destination : elle a basculé exprès pour l'ouvrir.
  const shownMember = useRef(userId);
  useEffect(() => {
    const before = shownMember.current;
    shownMember.current = userId;
    if (!before || !userId || before === userId) return;
    if (isExternalPath(location.pathname) || location.pathname === PUSH_OPEN_PATH) return;
    navigate('/', { replace: true });
  }, [userId]); // seul le changement d'adhésion compte
  useEffect(() => {
    if (!userId || isExternalPath(location.pathname)) return;
    if (!resumeChecked.current) {
      resumeChecked.current = true;
      const resume = location.pathname === '/' && !location.search ? pageToResume(userId) : undefined;
      if (resume) {
        navigate(resume, { replace: true });
        return;
      }
    }
    rememberPage(userId, `${location.pathname}${location.search}`);
  }, [userId, location.pathname, location.search, navigate]);

  // Pages publiques et espaces dédiés : accessibles sans session interne.
  // Même règle de reconnaissance que le bootstrap de session, sinon les deux
  // divergent — c'est ce qui rendait « /fournisseurs » inatteignable.
  // Session being read: the start composition, exactly under the static start
  // screen of index.html, so nothing moves when that screen fades out.
  if (loading && !external) return <BrandLoader variant={startupVariant()} />;

  return (
    <SignOutProvider><StepUpProvider><VersionGate><MissingFileBoundary>
    <Suspense fallback={<Spinner />}>
    <Routes location={overlay ?? location}>
      {/* Pages publiques, pages de compte à lien et espaces dédiés */}
      {externalRouteElements()}
      {/* Application interne */}
      {!me ? (
        <Route path="*" element={<LoginPage />} />
      ) : suspended ? (
        <>
          {/* Récupération des données par le patron (décision 12) : la seule autre page. */}
          <Route path={DATA_RECOVERY_PATH} element={<DataRecoveryPage />} />
          <Route path="*" element={<SuspendedScreen />} />
        </>
      ) : (
        <Route element={<AppShell />}>
          {internalRoutes}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
    {overlay ? (
      <SettingsDialog
        onClose={() => navigate(`${overlay.pathname}${overlay.search}${overlay.hash}`, { state: overlay.state })}
      >
        <Routes>{internalRoutes}</Routes>
      </SettingsDialog>
    ) : null}
    </Suspense>
    </MissingFileBoundary></VersionGate></StepUpProvider></SignOutProvider>
  );
}
